import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  VStack,
  HStack,
  Text,
  Input,
  Button,
  Alert,
  AlertIcon,
  useToast,
  Card,
  CardBody,
  CardHeader,
  FormControl,
  FormLabel,
  Spinner,
  Divider,
  SimpleGrid,
  Badge,
  Link,
  Image
} from '@chakra-ui/react';
import { LockIcon, CheckCircleIcon } from '@chakra-ui/icons';

const SecureDocument = () => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const toast = useToast();

  // Check if user is already authenticated (stored in sessionStorage)
  useEffect(() => {
    const isAuthenticated = sessionStorage.getItem('secure_doc_authenticated');
    if (isAuthenticated === 'true') {
      setAuthenticated(true);
    }
  }, []);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!password) {
      toast({
        title: 'Password required',
        description: 'Please enter the access password',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);

    try {
      // Simple password check (in production, this should be more secure)
      if (password === 'athome2024') {
        setAuthenticated(true);
        sessionStorage.setItem('secure_doc_authenticated', 'true');
        
        toast({
          title: 'Access granted!',
          description: 'Welcome to the secure document',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Access denied',
          description: 'Incorrect password. Please try again or request a new password.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error verifying password:', error);
      toast({
        title: 'Error',
        description: 'There was an error verifying your password. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setAuthenticated(false);
    sessionStorage.removeItem('secure_doc_authenticated');
    setPassword('');
  };

  // Password entry form
  if (!authenticated) {
    return (
      <Box minH="100vh" bg="gray.50" py={12}>
        <Container maxW="md">
          <VStack spacing={8} align="stretch">
            
            {/* Header */}
            <VStack spacing={4} textAlign="center">
              <Box p={4} bg="blue.100" borderRadius="full">
                <LockIcon boxSize={8} color="blue.600" />
              </Box>
              <Heading size="xl" color="blue.600">
                Secure Document Access
              </Heading>
              <Text color="gray.600" fontSize="lg">
                Enter your access password to view the secure document
              </Text>
            </VStack>

            {/* Password Form */}
            <Card>
              <CardBody p={8}>
                <form onSubmit={handlePasswordSubmit}>
                  <VStack spacing={6}>
                    <FormControl>
                      <FormLabel>Access Password</FormLabel>
                      <Input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your access password"
                        size="lg"
                        bg="white"
                        border="2px"
                        borderColor="gray.200"
                        _hover={{ borderColor: "blue.300" }}
                        _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)" }}
                      />
                    </FormControl>

                    <Button
                      type="submit"
                      colorScheme="blue"
                      size="lg"
                      w="100%"
                      isLoading={loading}
                      loadingText="Verifying..."
                      spinner={<Spinner size="sm" />}
                    >
                      Access Document
                    </Button>
                  </VStack>
                </form>
              </CardBody>
            </Card>

            {/* Help Section */}
            <Alert status="info">
              <AlertIcon />
              <Box>
                <Text fontSize="sm">
                  <strong>Don't have a password?</strong> 
                  <Link color="blue.500" href="/request-password" ml={2}>
                    Request access here
                  </Link>
                </Text>
              </Box>
            </Alert>

          </VStack>
        </Container>
      </Box>
    );
  }

  // Authenticated content - Document display
  return (
    <Box minH="100vh" bg="gray.50">
      
      {/* Header with logout */}
      <Box bg="white" shadow="sm" borderBottom="1px" borderColor="gray.200">
        <Container maxW="1200px">
          <HStack h={16} justify="space-between" align="center">
            <HStack spacing={3}>
              <CheckCircleIcon color="green.500" />
              <Text fontWeight="bold" color="gray.800">
                AtHomeBloodDraw.com - Secure Document
              </Text>
              <Badge colorScheme="green">Authenticated</Badge>
            </HStack>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              Logout
            </Button>
          </HStack>
        </Container>
      </Box>

      {/* Document Content */}
      <Container maxW="1200px" py={8}>
        <VStack spacing={8} align="stretch">
          
          {/* Title Section */}
          <Card>
            <CardHeader>
              <VStack spacing={4} textAlign="center">
                <Heading size="2xl" color="blue.600">
                  AtHomeBloodDraw.com
                </Heading>
                <Text fontSize="xl" color="gray.600">
                  Professional At-Home Blood Collection Services
                </Text>
              </VStack>
            </CardHeader>
          </Card>

          {/* Main Content - Replace this with actual PDF content */}
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8}>
            
            <Card>
              <CardHeader>
                <Heading size="lg">Our Services</Heading>
              </CardHeader>
              <CardBody>
                <VStack spacing={4} align="start">
                  <Text>
                    • Convenient at-home blood collection
                  </Text>
                  <Text>
                    • Certified phlebotomists
                  </Text>
                  <Text>
                    • Fast and accurate results
                  </Text>
                  <Text>
                    • Insurance accepted
                  </Text>
                  <Text>
                    • Same-day appointments available
                  </Text>
                </VStack>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <Heading size="lg">Contact Information</Heading>
              </CardHeader>
              <CardBody>
                <VStack spacing={4} align="start">
                  <Text>
                    <strong>Phone:</strong> +1 844-718-7800
                  </Text>
                  <Text>
                    <strong>Email:</strong> contact@athomeblooddraw.com
                  </Text>
                  <Text>
                    <strong>Website:</strong> www.athomeblooddraw.com
                  </Text>
                  <Text>
                    <strong>Hours:</strong> Monday - Friday, 7 AM - 7 PM
                  </Text>
                  <Text>
                    <strong>Coverage Area:</strong> Nationwide Service
                  </Text>
                </VStack>
              </CardBody>
            </Card>

          </SimpleGrid>

          {/* Additional Content Section */}
          <Card>
            <CardHeader>
              <Heading size="lg">About Our Service</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={4} align="start">
                <Text lineHeight="1.8">
                  AtHomeBloodDraw.com provides professional, convenient blood collection services 
                  in the comfort of your own home. Our certified phlebotomists are trained to 
                  provide the highest quality care while ensuring your safety and comfort.
                </Text>
                
                <Divider />
                
                <Text lineHeight="1.8">
                  We work with leading laboratories and healthcare providers to ensure accurate 
                  results and timely delivery. Whether you need routine blood work, specialized 
                  testing, or ongoing monitoring, we're here to make the process as convenient 
                  as possible.
                </Text>

                <Divider />

                <Text lineHeight="1.8" fontStyle="italic" color="gray.600">
                  <strong>Note:</strong> This is a template layout. Please provide the actual 
                  content from your PDF file so I can populate this page with the correct information.
                </Text>
              </VStack>
            </CardBody>
          </Card>

        </VStack>
      </Container>
    </Box>
  );
};

export default SecureDocument; 